import { Icon } from '@iconify/react'
import React, { type FC, useState } from 'react'
import tw from 'twin.macro'

// Extraindo estilos repetitivos para manter o código limpo (Princípio DRY)
const inputStyles = tw`
  w-full rounded-lg bg-white px-4 py-3 text-gray-900
  focus:outline-none focus:ring-2 focus:ring-indigo-400
  dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-400
`

const TheContactBanner: FC<JSX.IntrinsicElements['section']> = (props) => {
  const [showForm, setShowForm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setSuccess(false)
    setError(false)

    const form = e.currentTarget

    try {
      const res = await fetch('https://formspree.io/f/xwvvbgpg', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      })

      if (!res.ok) throw new Error()

      setSuccess(true)
      form.reset()
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  // Função para garantir que o formulário "resete" visualmente ao voltar
  const handleBack = () => {
    setShowForm(false)
    setSuccess(false)
    setError(false)
  }

  return (
    <section
      tw="
        mx-auto w-full max-w-4xl rounded-2xl border border-indigo-800 bg-indigo-600 px-12 py-10
        text-center text-gray-100 md:text-left
        dark:border-indigo-600 dark:bg-indigo-300 dark:text-gray-900
      "
      {...props}
    >
      {!showForm ? (
        // --- TELA INICIAL ---
        <>
          <h2 tw="text-3xl/[150%] font-bold md:text-5xl">Entre em contato</h2>
          <p tw="my-2 text-gray-200 md:text-lg dark:text-gray-800">
            Estou em busca de novos desafios para aplicar meus conhecimentos em
            tecnologia. Se você tem uma oportunidade ou projeto em mente, vamos
            conversar.
          </p>
          <div tw="mt-6 flex flex-col space-y-4 md:flex-row md:space-x-4 md:space-y-0">
            <button
              onClick={() => setShowForm(true)}
              tw="
                flex h-12 items-center justify-center rounded-lg bg-gray-100 px-6
                text-center text-sm font-semibold text-gray-900 ring-offset-2
                hover:bg-gray-200 focus:outline-none focus:ring md:inline-flex
                dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800
              "
            >
              Entrar em contato
              <Icon icon={'heroicons-outline:pencil-alt'} tw="ml-4 size-6" />
            </button>

            <a
              href="/resume.pdf"
              target="_blank"
              rel="noopener noreferrer"
              tw="
                flex h-12 items-center justify-center rounded-lg bg-gray-100 px-6
                text-center text-sm font-semibold text-gray-900 ring-offset-2
                hover:bg-gray-200 focus:outline-none focus:ring md:inline-flex
                dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800
              "
            >
              Baixar currículo
              <Icon
                icon={'heroicons-outline:arrow-down-tray'}
                tw="ml-4 size-6"
              />
            </a>
          </div>
        </>
      ) : (
        // --- TELA DO FORMULÁRIO ---
        <>
          <div tw="flex items-center justify-between">
            <h2 tw="text-3xl/[150%] font-bold md:text-5xl">
              Envie sua mensagem
            </h2>
            <button
              onClick={handleBack}
              tw="text-sm underline opacity-80 hover:opacity-100"
            >
              Voltar
            </button>
          </div>

          <p tw="my-2 text-gray-200 md:text-lg dark:text-gray-800">
            Preencha os campos abaixo e retornarei o mais breve possível.
          </p>

          <form onSubmit={handleSubmit} tw="mt-6 space-y-4">
            <input
              name="name"
              required
              placeholder="Seu nome"
              aria-label="Seu nome"
              css={inputStyles}
            />
            <input
              name="email"
              type="email"
              required
              placeholder="Seu e-mail"
              aria-label="Seu e-mail"
              css={inputStyles}
            />
            <textarea
              name="message"
              required
              placeholder="Sua mensagem"
              aria-label="Sua mensagem"
              rows={4}
              css={inputStyles}
            />

            <button
              type="submit"
              disabled={loading}
              tw="
                w-full rounded-lg bg-gray-100 py-3 font-semibold text-gray-900
                hover:bg-gray-200 disabled:opacity-50
                dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800
              "
            >
              {loading ? 'Enviando...' : 'Enviar mensagem'}
            </button>

            {/* CORREÇÃO DE CONTRASTE: 
                Usei dark:text-green-900 (verde escuro) para aparecer bem no fundo roxo claro do modo dark.
                Usei text-green-200 (verde claro) para aparecer bem no fundo roxo escuro do modo light.
            */}
            {success && (
              <div tw="mt-2 rounded bg-green-500/20 p-2 text-center font-bold text-green-200 dark:bg-green-600/20 dark:text-green-900">
                Mensagem enviada com sucesso!
              </div>
            )}

            {error && (
              <div tw="mt-2 rounded bg-red-500/20 p-2 text-center font-bold text-red-200 dark:bg-red-600/20 dark:text-red-900">
                Erro ao enviar. Tente novamente.
              </div>
            )}
          </form>
        </>
      )}
    </section>
  )
}

export default TheContactBanner
